import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { map, take } from 'rxjs/operators';
import { forkJoin, of, Subscription } from 'rxjs';
import { Subject } from '../../../shared/models/subject';
import { CategoryService } from '../../../shared/services/category.service';
import { MatDialog } from '@angular/material/dialog';
import { RelationshipService } from 'app/shared/services/relationship.service';
import { SubjectLink } from 'app/shared/models/subject-link';
import { SubjectService } from 'app/shared/services/subject.service';
import { SubjectListComponent } from './subject-list/subject-list.component';

@Component({
  selector: 'linked-subjects-list',
  template: `<subject-list #linkedsubjectlist  style="overflow-y: auto; height: 100%;"
            [subjects]="subject"
            [allowEdit]="false"
            [allowDelete]="false"
            [allowEditRelationship]="false"
            [allowSelect]="false"
            [readOnly]="true"
            [noContentMessage] = "noContentMessage"
        ></subject-list>`,
})
export class LinkedSubjectsListComponent implements OnInit, OnDestroy {

  _subject: Subject;
  title: string;
  primaryRelationships: string;
  noContentMessage = 'There are no linked subjects';

  private onSubjectChanged: Subscription;

  @Input() set subject(val: Subject) {
    if (val) {

      this.onSubjectChanged = this.subjectService.onItemChanged.subscribe((_) => {
        if (this._subject) {
          this.update(this._subject);
        }
      });

      this.update(val);
    }
  }

  @ViewChild('linkedsubjectlist') subjectList: SubjectListComponent;

  constructor(
    private relationshipService: RelationshipService,
    private categoryService: CategoryService,
    private subjectService: SubjectService,
    public dialog: MatDialog) {
  }

  ngOnInit() {
    this.title = 'Linked Subjects';
  }

  ngOnDestroy() {
    this.onSubjectChanged?.unsubscribe();
  }

  update(subject: Subject) {
    this._subject = subject;
    if (subject) {
      // get primary relationship id
      if (subject.categoryId) {
        this.categoryService.getById(subject.categoryId).subscribe((category) => {
          this.primaryRelationships = category.primaryRelationshipId;
        });
      }

      const hydrate$ = [];
      const subjectLinks = [];

      // re-get subject wih links
      this.subjectService.getSubjectWithLinks(this._subject).pipe(take(1)).subscribe(s => {
        this._subject = s;
        this._subject.subjectLinks.forEach((item) => {
          // check if has a linked subject and not already added to array of linked subjects with the same relationship
          if (item.linkedSubject && (subjectLinks.findIndex((x) => ((x.linkedSubject.id === item.linkedSubject.id) && (x.relationshipId === item.relationshipId))) < 0)) {
            // we need to calc required subjectData.displayValues for subject in case we need them for subject summary
            hydrate$.push(this.subjectService.hydrate(item.linkedSubject).pipe(take(1), map((_) => {
              this.relationshipService.getRelationships(subject.subjectTypeId, item.linkedSubject.subjectTypeId).pipe(take(1)).subscribe((relationships) => {
                const subjectLink = new SubjectLink(item);
                subjectLink.possibleRelationships = relationships.filter((i) => i.id !== item.relationshipId);
                subjectLink.primary = (this.primaryRelationships && ((',' + this.primaryRelationships + ',').includes(',' + item.relationshipId + ',')));
                subjectLinks.push(subjectLink);
              });
            })));
          }
        });

        // push a random observable onto the array - this ensures that we have something for the forkJoin
        hydrate$.push(of(subject));

        // use forkJoin to wait for all the observables in the hydrate$ array to complete, then set the subjectList.data
        forkJoin(hydrate$).pipe(take(1)).subscribe((_) => {
          this.subjectList.subjects = subjectLinks.sort((a, b) => a.linkedSubject.subjectType.name.localeCompare(b.linkedSubject.subjectType.name || a.id - b.id));
        });
      });
    }
  }
}
